/**
 * Ortak çalışma tasarımı sınıflandırıcısı.
 *
 * Yalnızca başlık + özet + Yöntem bölümüne bakar; kaynakça/atıf metni tasarımı
 * belirlemez. Laboratuvar ve hayvan çalışmaları insan kılavuzlarına zorlanmaz.
 */

export type StudyDesignKind =
	| 'rct'
	| 'observational'
	| 'systematic-review'
	| 'case'
	| 'unknown';

export type StudyScope = 'human' | 'animal' | 'lab' | 'unknown';

export interface StudyClassification {
	design: StudyDesignKind;
	scope: StudyScope;
	evidence: string;
	confidence: 'high' | 'medium' | 'low';
}

function cutReferences(text: string): string {
	const m = /(?:^|\n)\s*(?:references|kaynakça|kaynaklar|bibliography)\s*[:.]?\s*(?:\n|$)/i.exec(text);
	return m ? text.slice(0, m.index) : text;
}

function methodScope(text: string): string {
	const body = cutReferences(text);
	const start = /(?:^|\n)\s*(?:\d+[.)]?\s*)?(?:methods?|yöntem(?:ler)?|materyal\s+ve\s+yöntem|gereç\s+ve\s+yöntem|araştırma\s+yöntemi|material\s+and\s+methods)\b[^\n]*\n?/i.exec(
		body
	);
	if (!start) return body.slice(0, 12000);
	const from = start.index + start[0].length;
	const tail = body.slice(from);
	const end = /(?:^|\n)\s*(?:\d+[.)]?\s*)?(?:results?|bulgular|discussion|tartışma|conclusion|sonuç|references|kaynakça)\b/i.exec(
		tail
	);
	const methods = end ? tail.slice(0, end.index) : tail;
	const titleAbstract = body.slice(0, start.index);
	return `${titleAbstract}\n${methods}`.slice(0, 16000);
}

export function classifyStudy(text: string): StudyClassification {
	const scopeText = methodScope(text).toLowerCase();

	const animal = /(?:\bmice\b|\brats?\b|\bmurine\b|\banimals?\b|\bprimate|hayvan|fare|sıçan|deney\s+hayvanı)/i.test(
		scopeText
	);
	const lab = /(?:electrode|electrolysis|electrochemical|spectroscop|chromatograph|catalyst|polymer|synthesis|corrosion|titration|in\s+vitro|cell\s+line|cell\s+culture|simulation|electrolyte|\belectrochem|elektrot|elektroliz|spektroskopi|katalizör|polimer|sentez|korozyon|titrasyon|simülasyon)/i.test(
		scopeText
	);
	const human = /(?:informed\s+consent|bilgilendirilmiş\s+onam|onam\s+formu|ethics\s+committee|participants?|patients?|volunteers?|respondents?|human\s+subjects|katılımcı|araştırmaya\s+katıl|hasta|gönüllü|onam|etik\s+kurul|denek|örneklem|anket|görüşme|öğretmen|öğrenci|ölçek\s+uyarlama)/i.test(
		scopeText
	);
	const strongHuman = /(?:bilgilendirilmiş\s+onam|onam\s+formu|informed\s+consent|katılımcı|araştırmaya\s+katıl|ölçek\s+uyarlama|gönüllü|institutional\s+review\s+board|\bIRB\b|etik\s+onay)/i.test(
		scopeText
	);
	const ethicsNa = /(?:(?:institutional\s+review\s+board|\bIRB\b|informed\s+consent|ethics|etik|onam)[^.\n]{0,90}(?:not\s+applicable|n\/a|uygulanamaz|gerekli\s+değil)|(?:not\s+applicable|n\/a|uygulanamaz)[^.\n]{0,60}(?:IRB|consent|ethics|etik|onam))/i.test(
		scopeText
	);

	// Açık insan kanıtı (onam/katılımcı/ölçek uyarlama) hayvan/lab
	// göstergelerinden önce gelir; "Not applicable" bunu geçersiz kılar.
	const scope: StudyScope =
		strongHuman && !ethicsNa
			? 'human'
			: animal
				? 'animal'
				: lab
					? 'lab'
					: human
						? 'human'
						: 'unknown';

	if (scope === 'animal' || scope === 'lab') {
		return {
			design: 'unknown',
			scope,
			evidence: scope === 'animal' ? 'animal study indicators' : 'laboratory/engineering indicators',
			confidence: 'high'
		};
	}

	const rctStrong = /(?:randomi[sz]ed\s+controlled\s+trial\s+(?:was|were)\s+(?:conducted|performed)|this\s+(?:study|trial)\s+was\s+(?:a\s+)?randomi[sz]ed|\b(?:participants?|patients?)\s+were\s+random(?:ly)?\s+(?:assigned|allocated)|\bRCT\b|random\s+assignment|randomi[sz]ation|randomly\s+(?:assigned|allocated)|assigned\s+randomly|randomize\s+kontrollü|rastgele\s+atama)/i.exec(
		scopeText
	);
	const rctPhrase = rctStrong ? null : /randomi[sz]ed\s+(?:controlled\s+)?trial/i.exec(scopeText);
	const rctPhraseContext =
		rctPhrase && !/systematic\s+review|meta[\s-]?analys|trials?\s+were\s+(?:included|identified)|eligible\s+trials/i.test(
			scopeText.slice(Math.max(0, rctPhrase.index - 140), rctPhrase.index + 220)
		);
	const rct = rctStrong ?? (rctPhraseContext ? rctPhrase : null);
	if (rct) {
		return {
			design: 'rct',
			scope: scope === 'unknown' ? 'human' : scope,
			evidence: rct[0],
			confidence: 'high'
		};
	}

	const systematic = /(?:systematic\s+review|meta[\s-]?analys|scoping\s+review|sistematik\s+derleme|meta[\s-]?analiz)/i.exec(
		scopeText
	);
	if (systematic) {
		return {
			design: 'systematic-review',
			scope: scope === 'human' ? 'human' : 'unknown',
			evidence: systematic[0],
			confidence: 'high'
		};
	}

	const observational = /(?:cohort|cross[\s-]?sectional|case[\s-]?control|survey\s+(?:study|design)|kohort|kesitsel|vaka[\s-]?kontrol|ilişkisel|korelasyonel|korelasyon|correlational|correlation\s+study|mediation|mediating|moderation|moderating|path\s+analysis|structural\s+equation|\bSEM\b|aracılık|düzenleyici|yol\s+analizi|descriptive\s+(?:study|design)|tanımlayıcı|betimsel|tarama|anket)/i.exec(
		scopeText
	);
	if (observational) {
		return {
			design: 'observational',
			scope: scope === 'unknown' ? 'human' : scope,
			evidence: observational[0],
			confidence: 'medium'
		};
	}

	const caseStudy = /(?:case\s+(?:study|series)|vaka\s+(?:çalışması|serisi))/i.exec(scopeText);
	if (caseStudy) {
		return {
			design: 'case',
			scope: scope === 'unknown' ? 'human' : scope,
			evidence: caseStudy[0],
			confidence: 'medium'
		};
	}

	return { design: 'unknown', scope, evidence: '', confidence: 'low' };
}
